import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import categories from "../data/categories";
import { useLanguage } from "../context/LanguageContext";
import translations from "../i18n/translations";

export default function Services() {
  const [serviceType, setServiceType] = useState("entreprises");
  const [openService, setOpenService] = useState(null);
  const { lang } = useLanguage();
  const t = translations[lang];
  const navigate = useNavigate();

  const data = categories[serviceType];

  return (
    <>
      {/* ================= HERO ================= */}
      <section className="bg-gray-50 py-24 text-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mx-auto space-y-6"
        >
          <h2 className="text-4xl md:text-5xl font-extrabold text-red-600">
            {t.home.servicesTitle}
          </h2>

          <p className="text-2xl md:text-3xl font-light italic text-gray-700">
            {t.home.heroSubtitle5}
          </p>

          <div className="uppercase tracking-wide text-red-400 font-semibold">
            {t.home.heroBtnServices}
          </div>
        </motion.div>
      </section>

      {/* ================= SWITCH ================= */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex justify-center mb-12">
          <div className="bg-gray-100 rounded-full p-1 flex shadow-inner">
            {["entreprises", "prive"].map((type) => (
              <button
                key={type}
                onClick={() => setServiceType(type)}
                className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 ${
                  serviceType === type
                    ? "bg-red-600 text-white shadow-lg scale-105"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {type === "entreprises" ? "Entreprises" : "Particuliers"}
              </button>
            ))}
          </div>
        </div>

        {/* ================= INTRO ================= */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h3 className="text-2xl md:text-3xl font-bold mb-3">
            {data.intro[lang].title}
          </h3>
          <p className="text-gray-600 text-base md:text-lg leading-relaxed">
            {data.intro[lang].text}
          </p>
        </div>

        {/* ================= SERVICES GRID ================= */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.services[lang].map((service, i) => (
            <motion.div
              key={i}
              whileHover={{ y: -5 }}
              transition={{ duration: 0.3 }}
              onClick={() => setOpenService(service)}
              className="relative h-60 rounded-2xl overflow-hidden cursor-pointer group shadow-md"
            >
              <img
                src={`/images/services/${service.image}`}
                alt={service.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />

              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/55 transition" />

              <div className="relative z-10 h-full flex flex-col justify-end p-4 text-white">
                <h4 className="text-lg md:text-xl font-bold mb-1">
                  {service.title}
                </h4>
                <p className="text-sm md:text-base opacity-90">
                  {service.short}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= MODAL (FIXED MOBILE SCROLL) ================= */}
      <AnimatePresence>
        {openService && (
          <motion.div
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4 py-10 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpenService(null)}
          >
            <motion.div
              className="bg-white rounded-3xl max-w-lg w-full p-6 md:p-8 relative max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="sticky top-0 float-right text-xl text-gray-500 hover:text-black"
                onClick={() => setOpenService(null)}
              >
                ✕
              </button>

              <h3 className="text-2xl font-semibold mb-4">
                {openService.title}
              </h3>

              <p className="text-gray-600 mb-6 leading-relaxed">
                {openService.full}
              </p>

              <button
                onClick={() => navigate("/contact")}
                className="w-full py-3 rounded btn-gold font-semibold"
              >
                {t.home.specialOfferBtn}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}